import { AiChatMessageMode } from '@server/ai';
import type { AgentsHomeState } from './agentsHomeReducer';
import { agentsHomeInitialState } from './agentsHomeReducer';
import type { RootState } from '../../reducers';

const VALID_MODES = new Set<string>(Object.values(AiChatMessageMode));

/**
 * Sanitizes a persisted advancedMode value that may be stale or unknown
 * (any string not in {@link AiChatMessageMode}).
 */
const normalizeAdvancedMode = (value: unknown): AiChatMessageMode =>
  typeof value === 'string' && VALID_MODES.has(value) && value !== AiChatMessageMode.None
    ? (value as AiChatMessageMode)
    : AiChatMessageMode.None;

const normalizeStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/**
 * Returns the subset of AgentsHomeState to persist to localStorage,
 * with any stale values sanitized.
 * Used at boot time, before live entity data is available — type/shape sanitization only.
 */
export const agentsHomePersistedStateLoadHandler = (state: AgentsHomeState) => ({
  ...agentsHomeInitialState,
  advancedMode: normalizeAdvancedMode(state.advancedMode),
  enabledMcpConnectionIds: normalizeStringArray(state.enabledMcpConnectionIds),
  expandedFolderIds: normalizeStringArray(state.expandedFolderIds),
});

/**
 * Builds the set of valid entities from Redux state.
 * Returns null when the data hasn't been fetched yet.
 */
const computeValidIds = (state: RootState) => {
  const connectors = state.mcpConnectors.connectors;
  const validMcpIds = connectors
    ? new Set(connectors.map((c) => c.connectionId).filter((id): id is string => !!id))
    : null;

  const agents = Object.values(state.agentsChats.agentIds);
  const anyAgentLoadedFolders = agents.some((a) => a.folders.data !== undefined);
  const validFolderIds = anyAgentLoadedFolders
    ? new Set(agents.flatMap((a) => a.folders.data?.map((f) => f.id) ?? []))
    : null;

  return { validMcpIds, validFolderIds };
};

/**
 * Filter persisted IDs against live entities.
 */
export const agentsHomePersistedStateSaveHandler = (state: RootState) => {
  const agentsHome = state.agentsHome;
  const { validMcpIds, validFolderIds } = computeValidIds(state);

  return {
    ...agentsHomeInitialState,
    advancedMode: normalizeAdvancedMode(agentsHome.advancedMode),
    enabledMcpConnectionIds: validMcpIds
      ? agentsHome.enabledMcpConnectionIds.filter((id) => validMcpIds.has(id))
      : normalizeStringArray(agentsHome.enabledMcpConnectionIds),
    expandedFolderIds: validFolderIds
      ? agentsHome.expandedFolderIds.filter((id) => validFolderIds.has(id))
      : normalizeStringArray(agentsHome.expandedFolderIds),
  };
};
